/**
 * Download Utility Functions
 * Provides reusable functions for downloading files with authentication support
 */

interface DownloadOptions {
  filename?: string;
  token?: string | null;
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

interface ProxiedDownloadOptions extends DownloadOptions {
  proxiedUrl?: boolean; // Whether to use getProxiedAudioUrl
}

/**
 * Direct download a file from a URL
 * Supports authenticated requests and creates a blob for download
 * 
 * @param url - The file URL to download
 * @param options - Download options (filename, token, callbacks)
 * 
 * @example
 * await handleDirectDownload('https://example.com/audio.mp3', {
 *   filename: 'my-audio.mp3',
 *   token: localStorage.getItem('authToken'),
 *   onSuccess: () => console.log('Downloaded!')
 * });
 */
export const handleDirectDownload = async (
  url: string,
  options: DownloadOptions = {}
): Promise<void> => {
  const { filename = 'download.mp3', token = null, onError, onSuccess } = options;

  try {
    if (!url) {
      throw new Error('URL is required for download');
    }

    console.log('🔽 [DOWNLOAD UTIL] Starting download:', { 
      filename, 
      urlPreview: url.substring(0, 80) + '...',
      hasToken: !!token 
    });

    // Fetch the file with authentication header if provided
    const fetchOptions = {
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      }
    };
    
    console.log('🔽 [DOWNLOAD UTIL] Fetch options:', fetchOptions);

    const response = await fetch(url, fetchOptions);
    
    console.log('🔽 [DOWNLOAD UTIL] Fetch response:', {
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
      contentType: response.headers.get('content-type'),
      contentLength: response.headers.get('content-length')
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
    }

    // Convert response to blob
    console.log('🔽 [DOWNLOAD UTIL] Converting response to blob...');
    const blob = await response.blob();

    if (blob.size === 0) {
      throw new Error('Downloaded file is empty');
    }

    console.log('✅ [DOWNLOAD UTIL] Blob created:', {
      size: blob.size,
      type: blob.type
    });

    // Create blob URL
    const blobUrl = window.URL.createObjectURL(blob);
    console.log('🔗 [DOWNLOAD UTIL] Blob URL created:', blobUrl);

    // Create and trigger download
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    
    // CRITICAL: Set these attributes to force download behavior
    link.setAttribute('download', filename);
    link.setAttribute('target', '_self'); // Prevent opening in new tab
    
    console.log('🔗 [DOWNLOAD UTIL] Link element created:', {
      href: link.href,
      download: link.download,
      target: link.target,
      hasDownloadAttribute: link.hasAttribute('download')
    });
    
    document.body.appendChild(link);
    console.log('🔗 [DOWNLOAD UTIL] Link appended to body, triggering click...');

    link.click();
    console.log('✅ [DOWNLOAD UTIL] Click triggered successfully');

    // Small delay before cleanup to ensure download starts
    await new Promise(resolve => setTimeout(resolve, 100));

    // Cleanup
    console.log('🧹 [DOWNLOAD UTIL] Cleaning up...');
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);

    console.log('✅ [DOWNLOAD UTIL] Download complete:', filename);
    
    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    console.error('❌ [DOWNLOAD UTIL] Download error:', err);
    
    if (onError) {
      onError(err);
    } else {
      throw err;
    }
  }
};

/**
 * Download audio file with proxy support
 * Automatically handles proxied S3 URLs and authentication
 * 
 * @param audioUrl - The S3 audio URL
 * @param filename - The filename for download
 * @param token - Authentication token (optional)
 * @param getProxiedUrl - Function to convert S3 URL to proxy URL
 * @param callbacks - Success/error callbacks
 * 
 * @example
 * await handleAudioDownload(
 *   'https://s3.amazonaws.com/bucket/audio.mp3',
 *   'my-audio.mp3',
 *   token,
 *   getProxiedAudioUrl,
 *   {
 *     onSuccess: () => showNotification('Downloaded!'),
 *     onError: (err) => showNotification('Failed: ' + err.message)
 *   }
 * );
 */
export const handleAudioDownload = async (
  audioUrl: string,
  filename: string,
  token: string | null,
  getProxiedUrl: (url: string) => string,
  callbacks?: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  }
): Promise<void> => {
  try {
    // Convert to proxied URL if it's an S3 URL
    const proxiedUrl = getProxiedUrl(audioUrl);
    
    await handleDirectDownload(proxiedUrl, {
      filename,
      token,
      onSuccess: callbacks?.onSuccess,
      onError: callbacks?.onError
    });
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    if (callbacks?.onError) {
      callbacks.onError(err);
    } else {
      throw err;
    }
  }
};

/**
 * Download multiple files sequentially
 * 
 * @param urls - Array of URLs to download
 * @param filenames - Array of filenames (should match urls length)
 * @param token - Authentication token
 * @param options - Download options
 * 
 * @example
 * await handleBatchDownload(
 *   ['url1.mp3', 'url2.mp3'],
 *   ['file1.mp3', 'file2.mp3'],
 *   token,
 *   { onSuccess: () => console.log('All downloaded!') }
 * );
 */
export const handleBatchDownload = async (
  urls: string[],
  filenames: string[],
  token: string | null,
  options: DownloadOptions = {}
): Promise<void> => {
  if (urls.length !== filenames.length) {
    throw new Error('URLs and filenames arrays must have the same length');
  }

  try {
    console.log('🔽 Starting batch download of', urls.length, 'files');

    for (let i = 0; i < urls.length; i++) {
      console.log(`Downloading ${i + 1}/${urls.length}: ${filenames[i]}`);
      await handleDirectDownload(urls[i], {
        filename: filenames[i],
        token
      });
      
      // Small delay between downloads to avoid overwhelming the browser
      if (i < urls.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }

    console.log('✅ All files downloaded successfully');
    
    if (options.onSuccess) {
      options.onSuccess();
    }
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    console.error('❌ Batch download error:', err);
    
    if (options.onError) {
      options.onError(err);
    } else {
      throw err;
    }
  }
};

/**
 * Download with automatic error notification
 * Requires a notification context function
 * 
 * @param url - File URL
 * @param filename - Filename for download
 * @param token - Auth token
 * @param showNotification - Notification function
 * 
 * @example
 * await handleDownloadWithNotification(
 *   audioUrl,
 *   'audio.mp3',
 *   token,
 *   showNotification
 * );
 */
export const handleDownloadWithNotification = async (
  url: string,
  filename: string,
  token: string | null,
  showNotification: (notification: {
    title: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  }) => void
): Promise<void> => {
  await handleDirectDownload(url, {
    filename,
    token,
    onSuccess: () => {
      showNotification({
        title: '✅ Downloaded',
        message: `${filename} downloaded successfully`,
        type: 'success'
      });
    },
    onError: (error) => {
      showNotification({
        title: '❌ Download Failed',
        message: error.message || 'Failed to download file',
        type: 'error'
      });
    }
  });
};